import type { ID } from '@octopus-do/octopus-project-core'
import { selectBlockParent } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { shallowEqual, useSelector } from 'react-redux'

interface ArrowSourceUI {
  // Чем рисуем: свой цвет стрелки, если он задан, иначе цвет источника.
  color: string
  // Страница, на которой живёт начало стрелки: сам источник, если стрелка
  // начинается со страницы, иначе страница блока-источника.
  nodeId: ID
  nodeVariant: string
  // Цвет источника отдельно — его показывает пункт «как у источника» в меню,
  // когда у стрелки своего цвета нет.
  sourceColor: string
}

/**
 * Начало стрелки — блок или сама страница (OCT-5140). Блочные селекторы на
 * странице отдают пустышку с colorId '', поэтому цвет и проверку варианта
 * страницы берём у той сущности, которой начало на самом деле принадлежит.
 *
 * Цвет источника — это дефолт, а не единственный вариант: у стрелки со своим
 * colorId он и рисуется (OCT-5222).
 */
export const useArrowSource = (
  startId: ID,
  colorId: ID = ''
): ArrowSourceUI => {
  const node = useSelector(
    migrationProjectSelector.getProjectNodeByIdUnsafe(startId),
    shallowEqual
  )

  const { colorId: blockColorId } = useSelector(
    migrationProjectSelector.getProjectBlockOrSymbolData(startId),
    shallowEqual
  )

  const blockParentIds = useSelector(
    selectBlockParent(startId),
    shallowEqual
  ) as ID[]

  const nodeId = node ? startId : (blockParentIds[0] ?? '')

  const { variant: nodeVariant } = useSelector(
    migrationProjectSelector.getProjectNodeById(nodeId)
  )

  const sourceColor = useSelector(
    migrationProjectSelector.getProjectColorForUI(
      node ? node.colorId : blockColorId
    )
  )

  // Пустой colorId сюда не попадает: для него цвет и так берётся у источника.
  const ownColor = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const color = colorId ? ownColor : sourceColor

  return { color, nodeId, nodeVariant, sourceColor }
}
